/* eslint-disable @typescript-eslint/no-explicit-any */
// src/composables/useSocketIO.ts
import { io, Socket } from "socket.io-client";
import { ref, onUnmounted } from "vue";
import { useChatStore } from "../store/chat";

export function useSocketIO(url: string, options: any = {}) {
  const socket = ref<Socket | null>(null);
  const isConnected = ref(false);
  // 持久化监听器注册表：每次 connect() 创建新 socket 时都会重新挂载
  const registeredListeners: { event: string; callback: (data: any) => void }[] = [];

  const cStore = useChatStore();

  // 连接 Socket
  const connect = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // 如果已存在 socket，先彻底清理，避免双连接
      if (socket.value) {
        socket.value.removeAllListeners();
        socket.value.disconnect();
        socket.value = null;
        isConnected.value = false;
      }

      const urlObj = new URL(url);
      const baseUrl = `${urlObj.protocol}//${urlObj.host}`;
      console.log("baseUrl", baseUrl);
      const path = urlObj.pathname; // 获取 /basic-api/websocket/socket.io

      socket.value = io(baseUrl, {
        transports: ["websocket", "polling"],
        path: path, // 关键：设置自定义路径
        query: {
          token: urlObj.searchParams.get("token"),
          channelId: urlObj.searchParams.get("channelId"),
          // source 仅客服模式带上（chat store 拼 URL 时按 role 决定是否附加），
          // 这里透传：无该参数时 undefined 不会被 socket.io 序列化到 query。
          ...(urlObj.searchParams.get("source")
            ? { source: urlObj.searchParams.get("source") }
            : {}),
        },
        ...options,
      });

      // 把注册表里的监听器挂到新 socket 上（每次重连都重新挂载，不清空注册表）
      for (const { event, callback } of registeredListeners) {
        socket.value.on(event, callback);
      }

      socket.value.on("connect", () => {
        isConnected.value = true;
        console.log("Socket.IO 连接成功");
        resolve();
      });

      socket.value.on("connect_error", (error) => {
        console.error("Socket.IO 连接失败:", error);
        reject(error);
      });

      socket.value.on("disconnect", () => {
        isConnected.value = false;
        console.log("Socket.IO 断开连接");
      });
    });
  };

  // 发送事件
  const emit = (event: string, data?: any) => {
    if (socket.value && isConnected.value) {
      socket.value.emit(event, data);
    }
  };

  // 监听事件：写入持久化注册表，并挂到当前 socket（如果已存在）。
  // 重连时 connect() 会从注册表把所有监听器重新挂到新 socket 上。
  const on = (event: string, callback: (data: any) => void) => {
    registeredListeners.push({ event, callback });
    if (socket.value) {
      socket.value.on(event, callback);
    }
  };

  // 移除监听
  const off = (event: string) => {
    socket.value?.off(event);
  };

  // 断开连接
  const disconnect = () => {
    if (socket.value) {
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
    }
  };

  // 自动清理
  onUnmounted(() => {
    disconnect();
  });

  return {
    connect,
    disconnect,
    emit,
    on,
    off,
    isConnected,
  };
}
